import React, {CSSProperties, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useState} from 'react'

import P from '../typography/P.js'
import IconButton, {IconButtonProps} from '../buttons/IconButton.js'
import {focusableClass, ensureFocusStyles} from '../styles.js'
import {getListItemBackground, surfaceColors, surfaceSizes} from './styles.js'

ensureFocusStyles()

interface ListItemProps {
    id?: string
    children?: ReactNode
    /** Style of the row; its width and background go to the outer element instead (unless wrapperStyle is given) */
    style?: CSSProperties
    /** Style of the outer element instead of the width and background from style */
    wrapperStyle?: CSSProperties
    size?: 'sm' | 'md'
    /**
     * Highlighted by the keyboard selection of the list it is in (Select, Input suggestions). A list that passes it
     * handles the keyboard itself, so the item is no tab stop then.
     */
    focused?: boolean
    /** Medium font weight */
    active?: boolean
    /** Grey text, still clickable */
    inactive?: boolean
    /** Grey text, not clickable */
    disabled?: boolean
    /** Icon class shown at the left */
    icon?: string
    /** Icon buttons at the right, shown while the item is hovered or has the focus */
    iconButtons?: IconButtonProps[]
    onClick?: (event: MouseEvent<HTMLDivElement>) => void
    onMouseDown?: (event: MouseEvent<HTMLDivElement>) => void
}

/**
 * A row in a list or menu with an optional icon and icon buttons. With onClick (and no focused) it is a button that
 * the keyboard reaches and Enter or Space clicks.
 */
export default function ListItem({id, children, style = {}, wrapperStyle, size = 'md', focused, active, inactive, disabled, icon, iconButtons, onClick, onMouseDown}: ListItemProps) {
    const [hover, setHover] = useState(false)
    const [focusWithin, setFocusWithin] = useState(false)

    const sizeKey = size === 'sm' ? 'sm' : 'md'
    const keyboardClickable = !!onClick && !disabled && focused === undefined

    // Leave out undefined values, so {color: danger ? red : undefined} keeps the default (or disabled) colour
    const {width, background, ...rowStyle} = Object.fromEntries(Object.entries(style).filter(([, value]) => value !== undefined)) as CSSProperties

    const itemStyle: CSSProperties = {
        display: 'flex',
        flexShrink: 0,
        width: '100%',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: surfaceSizes.listItem.paddingX[sizeKey],
        paddingRight: surfaceSizes.listItem.paddingX[sizeKey],
        minHeight: surfaceSizes.listItem.minHeight[sizeKey],
        fontWeight: disabled || !active || inactive ? 400 : 500,
        color: disabled || inactive ? surfaceColors.listItemTextInactive : surfaceColors.listItemText,
        background: getListItemBackground({hovered: hover || focused}),
        ...rowStyle
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        // Only the item itself, not an icon button in it
        if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
            return
        }

        // Space would scroll the page. A real click, so onClick gets a MouseEvent as for the mouse.
        event.preventDefault()
        event.currentTarget.click()
    }

    const onBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setFocusWithin(false)
        }
    }

    return (
        <div
            id={id}
            style={wrapperStyle || {width, background}}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setFocusWithin(true)}
            onBlur={onBlur}
        >
            <P
                style={itemStyle}
                role={keyboardClickable ? 'button' : undefined}
                tabIndex={keyboardClickable ? 0 : undefined}
                aria-disabled={disabled || undefined}
                className={keyboardClickable ? focusableClass : undefined}
                onClick={disabled ? undefined : onClick}
                onMouseDown={disabled ? undefined : onMouseDown}
                onKeyDown={keyboardClickable ? onKeyDown : undefined}
            >
                {icon && <i aria-hidden style={{marginRight: surfaceSizes.listItem.iconGap, fontSize: surfaceSizes.listItem.iconSize[sizeKey]}} className={icon} />}

                {children}

                {!!iconButtons &&
                    <div style={{marginLeft: surfaceSizes.listItem.iconButtonsGap, display: 'flex', flex: 1, justifyContent: 'flex-end'}}>
                        {(hover || focusWithin) && iconButtons.map((iconButton, index) => (
                            <IconButton key={index} {...iconButton} />
                        ))}
                    </div>
                }
            </P>
        </div>
    )
}
